import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

const GIVING_TUESDAY_MARK_PATH =
  "M789.971 0.00695801C800.821 -0.0940414 811.281 0.911082 821.631 2.41907L559.168 264.774L618.17 323.767L907.061 34.6906C930.371 50.0725 950.371 70.2806 965.851 93.6046L677.029 382.617L735.69 441.269L998.21 178.758C999.438 187.334 1000.4 195.911 1000.52 204.819L1000.63 206.606C1001.23 259.891 981.43 308.651 948.87 345.851L500.62 794.03L500.32 793.731L500.021 794.03L51.7725 345.851C19.2095 308.651 -0.590272 259.891 0.0126953 206.606L0.125 204.819C0.240187 195.911 1.19952 187.334 2.4248 178.758L264.947 441.269L323.609 382.616L34.7871 93.6046C50.2641 70.2806 70.2651 50.0725 93.5811 34.6906L382.468 323.766L441.472 264.773L179.01 2.41907C189.361 0.911097 199.814 -0.0940408 210.668 0.00695801L213.17 0.144653C265.649 1.26261 313.25 21.9436 349.263 54.8986L500.32 205.934L651.381 54.8986C687.389 21.9437 734.987 1.26276 787.468 0.144653L789.971 0.00695801ZM441.744 618.059L500.319 676.632L558.896 618.057L500.32 559.443L441.744 618.059ZM323.763 500.082L382.886 559.203L441.483 500.568L382.412 441.457L323.763 500.082ZM559.155 500.568L617.752 559.203L676.877 500.08L618.229 441.457L559.155 500.568ZM441.278 382.614L500.318 441.693L559.361 382.613L500.32 323.597L441.278 382.614Z";
const MARK_CENTER_X = 500.32;
const MARK_CENTER_Y = 397.5;
const MARK_HEIGHT = 795;

const GRID_SPACING = 63;
const MARK_SIZE = 23;
const RIPPLE_SPEED = 5;
const RIPPLE_WIDTH = 295;
const BEAT_AMPLITUDE = 0.26;
const BEAT_ALPHA = 0.12;
const REST_ALPHA_BY_THEME: Record<CanvasBackgroundTheme, number> = {
  dark: 0.16,
  light: 0.22,
  darkdull: 0.22,
  lightdull: 0.24,
};
const GRID_TILT_RADIANS = (45 * Math.PI) / 180;

export type RippleHeartsAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  frameStride?: number;
};

type GridCell = {
  x: number;
  y: number;
};

export const RippleHeartsAnimation: FC<RippleHeartsAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;
    const markPath = new Path2D(GIVING_TUESDAY_MARK_PATH);
    const restAlpha = REST_ALPHA_BY_THEME[theme];

    function markColor(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
        case "light":
          return `hsla(${hue}, 55%, 58%, ${alpha * 0.85})`;
        case "darkdull":
          return `hsla(0, 0%, 82%, ${alpha * 0.78})`;
        case "lightdull":
          return `hsla(0, 0%, 38%, ${alpha * 0.72})`;
        default:
          return `hsla(${hue}, 68%, 82%, ${alpha * 0.9})`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    let cells: GridCell[] = [];
    let edgeCells: GridCell[] = [];
    let centerCells: GridCell[] = [];
    let onScreenCells: GridCell[] = [];
    let isEdgeTurn = false;
    let originX = 0;
    let originY = 0;
    let elapsedFrames = 0;
    let maxDistance = 1;

    function startRipple(): void {
      isEdgeTurn = !isEdgeTurn;
      const preferredPool = isEdgeTurn ? edgeCells : centerCells;
      const pool = preferredPool.length > 0 ? preferredPool : onScreenCells;
      if (pool.length === 0) {
        return;
      }
      const origin = pool[Math.floor(Math.random() * pool.length)];
      originX = origin.x;
      originY = origin.y;
      elapsedFrames = 0;
      maxDistance = furthestCellDistance();
    }

    function furthestCellDistance(): number {
      let furthest = 1;
      for (const cell of cells) {
        const distance = Math.hypot(cell.x - originX, cell.y - originY);
        if (distance > furthest) {
          furthest = distance;
        }
      }
      return furthest;
    }

    function buildGrid(): void {
      const width = canvasEl.width;
      const height = canvasEl.height;
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.ceil((Math.hypot(width, height) / 2 + GRID_SPACING) / GRID_SPACING);
      const cos = Math.cos(GRID_TILT_RADIANS);
      const sin = Math.sin(GRID_TILT_RADIANS);
      const drawMargin = MARK_SIZE * (1 + BEAT_AMPLITUDE);

      cells = [];
      for (let row = -radius; row <= radius; row++) {
        for (let column = -radius; column <= radius; column++) {
          const gridX = column * GRID_SPACING;
          const gridY = row * GRID_SPACING;
          const cell = { x: centerX + gridX * cos - gridY * sin, y: centerY + gridX * sin + gridY * cos };
          const isDrawable =
            cell.x >= -drawMargin &&
            cell.x <= width + drawMargin &&
            cell.y >= -drawMargin &&
            cell.y <= height + drawMargin;
          if (isDrawable) {
            cells.push(cell);
          }
        }
      }

      onScreenCells = cells.filter((cell) => cell.x >= 0 && cell.x <= width && cell.y >= 0 && cell.y <= height);
      edgeCells = onScreenCells.filter(
        (cell) =>
          cell.x < 1.2 * GRID_SPACING ||
          cell.x > width - 1.2 * GRID_SPACING ||
          cell.y < 1.2 * GRID_SPACING ||
          cell.y > height - 1.2 * GRID_SPACING
      );
      centerCells = onScreenCells.filter(
        (cell) => cell.x > 0.3 * width && cell.x < 0.7 * width && cell.y > 0.3 * height && cell.y < 0.7 * height
      );
    }

    buildGrid();
    startRipple();

    function onResize(): void {
      const previousCenterX = canvasEl.width / 2;
      const previousCenterY = canvasEl.height / 2;
      resizeCanvas();
      buildGrid();
      originX += canvasEl.width / 2 - previousCenterX;
      originY += canvasEl.height / 2 - previousCenterY;
      maxDistance = furthestCellDistance();
    }

    window.addEventListener("resize", onResize);

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);
      elapsedFrames += frameSteps;
      if (elapsedFrames * RIPPLE_SPEED > maxDistance + RIPPLE_WIDTH) {
        startRipple();
      }
      const front = elapsedFrames * RIPPLE_SPEED;

      for (const cell of cells) {
        const lead = front - Math.hypot(cell.x - originX, cell.y - originY);
        const envelope = lead >= 0 && lead <= RIPPLE_WIDTH ? Math.sin((Math.PI * lead) / RIPPLE_WIDTH) : 0;
        const beatScale = 1 + BEAT_AMPLITUDE * envelope;
        const alpha = restAlpha + BEAT_ALPHA * envelope;
        const scale = (MARK_SIZE / MARK_HEIGHT) * beatScale;

        context.save();
        context.translate(cell.x, cell.y);
        context.scale(scale, scale);
        context.translate(-MARK_CENTER_X, -MARK_CENTER_Y);
        context.fillStyle = markColor(alpha);
        context.fill(markPath);
        context.restore();
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};
